/**
 * Board filters mirrored to the URL: `q`, `role_id`, and `lead` (the open
 * drawer). The shop filter lives in the shop store, which already writes
 * `shopId` to the URL — reading `window.location.search` as the base keeps
 * that param intact when the router rewrites the rest.
 */
import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useShopStore } from '@/lib/shop-store';

export interface BoardFilters {
  q: string;
  roleId: string;
  shopId: string;
  leadId: string | null;
}

type Patch = Partial<Pick<BoardFilters, 'q' | 'roleId' | 'leadId'>>;

const KEYS: Record<keyof Patch, string> = { q: 'q', roleId: 'role_id', leadId: 'lead' };

export function useBoardFilters() {
  const location = useLocation();
  const navigate = useNavigate();
  const shopId = useShopStore((s) => s.selectedShopId) ?? '';
  const setShop = useShopStore((s) => s.setShop);

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const filters: BoardFilters = useMemo(
    () => ({
      q: params.get('q') ?? '',
      roleId: params.get('role_id') ?? '',
      shopId,
      leadId: params.get('lead'),
    }),
    [params, shopId],
  );

  const update = useCallback(
    (patch: Patch, opts: { replace?: boolean } = {}) => {
      const next = new URLSearchParams(location.search);
      // The shop store writes `shopId` straight to window.location; carry it over.
      const liveShop = new URLSearchParams(window.location.search).get('shopId');
      if (liveShop && !next.has('shopId')) next.set('shopId', liveShop);
      for (const [k, v] of Object.entries(patch) as Array<[keyof Patch, string | null | undefined]>) {
        if (v) next.set(KEYS[k], v);
        else next.delete(KEYS[k]);
      }
      const search = next.toString();
      navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: opts.replace ?? true });
    },
    [navigate, location.pathname, location.search],
  );

  const clear = useCallback(() => {
    update({ q: '', roleId: '' });
    setShop(null);
  }, [update, setShop]);

  const hasActive = !!(filters.q || filters.roleId || filters.shopId);

  return { filters, update, setShop, clear, hasActive };
}
